/**
 * Cuántas notas trae un bloque de categoría, según la disposición que eligió el editor.
 *
 * Las cantidades ofrecidas son **múltiplos de lo que la plantilla dibuja de una vez**: la tira
 * muestra 4 por página y la lista 6, así que elegir 16 en una tira significa "cuatro páginas de 4", y
 * el bloque pagina con chevrones sin volver a pedirle nada a la API. La cuadrícula no pagina —crece
 * hacia abajo—, así que su paso no es una página sino una **fila** de 3.
 *
 * La regla vive acá y no en el panel ni en la API porque la usan los tres: el panel para ofrecer
 * solo valores válidos, la API para rechazar los que no lo son, y el sitio para saber cuántas
 * páginas dibujar. Dos implementaciones de esto serían un bloque que el panel deja guardar y el
 * sitio no sabe dibujar.
 */
/**
 * Lo que cada plantilla dibuja de una vez, y el paso con el que crece la cantidad: una **página** en
 * tira y lista, una **fila** en la cuadrícula (handoff §1.2: la tira 4, la lista 6).
 */
export const NOTAS_POR_PASO = {
    TIRA: 4,
    LISTA: 6,
    CUADRICULA: 3,
};
/**
 * Cap explícito de páginas para las plantillas que paginan. De acá salen las cantidades que se
 * pidieron —4/8/12/16 en tira y 6/12/18/24 en lista— y su tope duro de 24 notas por bloque.
 *
 * Es un número y no una lista abierta por dos razones: el payload de la portada crece con cada
 * bloque (9 × 24 notas viaja en cada miss de caché), y un lector que tiene que apretar el chevrón
 * más de cuatro veces ya no está mirando una portada.
 *
 * **La cuadrícula no lo usa**: no tiene chevrones que apretar, así que el argumento de arriba no le
 * aplica y su tope es propio (ver `MAXIMO_POR_BLOQUE`).
 */
export const PAGINAS_MAX_POR_BLOQUE = 4;
/**
 * El tope de notas de cada disposición. Las dos que paginan lo derivan del cap de páginas; la
 * cuadrícula lo tiene escrito porque 60 son 20 **filas**, no 20 páginas: el lector las recorre
 * scrolleando, que es lo que pidió el cliente para las secciones que quiere mostrar completas.
 */
export const MAXIMO_POR_BLOQUE = {
    TIRA: NOTAS_POR_PASO.TIRA * PAGINAS_MAX_POR_BLOQUE,
    LISTA: NOTAS_POR_PASO.LISTA * PAGINAS_MAX_POR_BLOQUE,
    CUADRICULA: 60,
};
/**
 * Si el bloque se recorre con chevrones o de corrido. Es una propiedad de la plantilla y no una
 * comparación suelta contra `'CUADRICULA'` porque la consultan el sitio (para no dibujar el riel) y
 * el panel (para decir "filas" en vez de "páginas"): si un día hay otra que no pagine, se agrega acá.
 */
export function plantillaPagina(plantilla) {
    return plantilla !== 'CUADRICULA';
}
/** Las cantidades que el panel ofrece para esa plantilla, de menor a mayor. */
export function cantidadesValidas(plantilla) {
    const paso = NOTAS_POR_PASO[plantilla];
    const cuantas = Math.floor(MAXIMO_POR_BLOQUE[plantilla] / paso);
    return Array.from({ length: cuantas }, (_, i) => paso * (i + 1));
}
export function esCantidadValida(plantilla, notas) {
    return cantidadesValidas(plantilla).includes(notas);
}
/**
 * La cantidad válida más cercana. Es lo que se usa cuando el editor cambia de plantilla y la
 * cantidad que tenía deja de existir: 8 en una tira pasa a 6 en lista, no vuelve al default.
 *
 * Los empates caen para abajo (9 → 6, no 12): agregar notas que nadie pidió cambia la portada más
 * que quitarlas.
 */
export function ajustarCantidad(plantilla, notas) {
    const opciones = cantidadesValidas(plantilla);
    return opciones.reduce((mejor, opcion) => Math.abs(opcion - notas) < Math.abs(mejor - notas) ? opcion : mejor);
}
/**
 * Cuántas páginas dibuja un bloque con esas notas. El sitio la usa para decidir si hacen falta
 * chevrones: con una sola página no se dibujan.
 *
 * La cuadrícula es siempre **una**: sus notas conviven en la misma grilla, por muchas filas que
 * ocupen. Preguntarle "cuántas páginas" y que conteste 20 haría aparecer un riel que no existe.
 */
export function paginasDelBloque(plantilla, cuantasNotas) {
    if (!plantillaPagina(plantilla))
        return 1;
    return Math.max(1, Math.ceil(cuantasNotas / NOTAS_POR_PASO[plantilla]));
}
/**
 * Cuántas filas ocupa el bloque: para la cuadrícula es lo que el panel le dice al editor ("20 filas
 * de 3"), y es también lo que crece hacia abajo en la home.
 */
export function filasDelBloque(plantilla, cuantasNotas) {
    return Math.max(1, Math.ceil(cuantasNotas / NOTAS_POR_PASO[plantilla]));
}
